import React, { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Mermaid from './Mermaid';
import QuizCard from './QuizCard';
import PollCard from './PollCard';
import { resizeImage } from '../utils/resizeImage';
import { IconEdit, IconClose, IconShare } from './Icons';
import './RightPanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// Resolve a stored image URL (relative /uploads/... or absolute) for <img src>.
function resolveImageSrc(value) {
  if (typeof value !== 'string') return '';
  return value.startsWith('http') ? value : `${SERVER_URL}${value}`;
}

// Match the first URL inside an arbitrary label so we can render the
// surrounding text as a clickable title and hide the raw URL.
const URL_RE = /https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]'"]/i;

function extractTitleAndUrl(text) {
  if (typeof text !== 'string') return null;
  const match = text.match(URL_RE);
  if (!match) return null;
  const url = match[0];
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + url.length);
  const title = (before + ' ' + after)
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-|:·•—–()[\]]+/, '')
    .replace(/[\s\-|:·•—–()[\]]+$/, '')
    .trim();
  return { title, url };
}

function LinkedLabel({ text, className = '' }) {
  if (typeof text !== 'string' || !text) {
    return <span className={className}>{text}</span>;
  }
  const parsed = extractTitleAndUrl(text);
  if (!parsed) return <span className={className}>{text}</span>;
  const display = parsed.title || parsed.url;
  return (
    <a
      className={`${className} is-link`.trim()}
      href={parsed.url}
      target="_blank"
      rel="noopener noreferrer"
      title={parsed.url}
    >
      {display}
    </a>
  );
}

function RightPanel({
  artifacts,
  todos,
  decisions = [],
  shortLists = [],
  siblingThreadSummary = [],
  decisionSources = {},
  onAddArtifact,
  onRemoveArtifact,
  onUpdateArtifact,
  onGetArtifactVersions,
  onAddTodo,
  onToggleTodo,
  onRemoveTodo,
  onLogDecision,
  onRemoveDecision,
  onUpdateDecisionStatus,
  onAddShortList,
  onAddShortListItem,
  onRemoveShortListItem,
  onToggleShortListItemPin,
  onRemoveShortList,
  onJumpToMessage,
  onVotePoll,
  onClosePoll,
  onToggleChecklistItem,
  currentUserId,
  workspaceId,
}) {
  const [activeTab, setActiveTab] = useState('decisions');
  const [showArtifactForm, setShowArtifactForm] = useState(false);
  const [artifactTitle, setArtifactTitle] = useState('');
  const [artifactContent, setArtifactContent] = useState('');
  const [artifactType, setArtifactType] = useState('note');
  const [artifactImgUploading, setArtifactImgUploading] = useState(false);
  const [artifactImgError, setArtifactImgError] = useState('');
  const artifactImgRef = useRef(null);
  const [todoInput, setTodoInput] = useState('');
  const [expandedArtifact, setExpandedArtifact] = useState(null);
  const [editingArtifact, setEditingArtifact] = useState(null); // { id, title, content }
  const [versionHistory, setVersionHistory] = useState(null); // { artifactId, versions }
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [decisionTitle, setDecisionTitle] = useState('');
  const [decisionDescription, setDecisionDescription] = useState('');
  // decisionId -> { url, copied } | { error: true } for the public share links.
  const [decisionShares, setDecisionShares] = useState({});

  // Create (idempotent) a public share link for a decision and copy it to the
  // clipboard. Auth follows the DecisionGraphPanel pattern: bearer token from
  // localStorage against the workspace-scoped REST endpoint.
  const handleShareDecision = async (decisionId) => {
    if (!workspaceId) return;
    try {
      const token = localStorage.getItem('symphonate-token');
      const res = await fetch(
        `${SERVER_URL}/api/workspaces/${workspaceId}/decisions/${decisionId}/share`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('share failed');
      const data = await res.json();
      let copied = false;
      try {
        await navigator.clipboard.writeText(data.url);
        copied = true;
      } catch {
        // Clipboard unavailable (insecure context) — still show the link.
      }
      setDecisionShares((prev) => ({ ...prev, [decisionId]: { url: data.url, copied } }));
      if (copied) {
        setTimeout(() => {
          setDecisionShares((prev) =>
            prev[decisionId]
              ? { ...prev, [decisionId]: { ...prev[decisionId], copied: false } }
              : prev,
          );
        }, 2000);
      }
    } catch {
      setDecisionShares((prev) => ({ ...prev, [decisionId]: { error: true } }));
    }
  };

  // Revoke the public link — the URL 404s immediately.
  const handleUnshareDecision = async (decisionId) => {
    if (!workspaceId) return;
    try {
      const token = localStorage.getItem('symphonate-token');
      await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/decisions/${decisionId}/share`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Best-effort; clear local state either way.
    }
    setDecisionShares((prev) => {
      const next = { ...prev };
      delete next[decisionId];
      return next;
    });
  };
  const [shortListItemDrafts, setShortListItemDrafts] = useState({}); // listId -> draft text
  const [aiContextOpen, setAiContextOpen] = useState(true);

  const resetArtifactForm = () => {
    setArtifactTitle('');
    setArtifactContent('');
    setArtifactType('note');
    setArtifactImgError('');
    setShowArtifactForm(false);
  };

  const handleAddArtifact = (e) => {
    e.preventDefault();
    if (!artifactTitle.trim()) return;
    // A short list is a kind of artefact, but it's stored/managed separately
    // (its own add/item/pin events), so route it through onAddShortList. The
    // content textarea holds the initial candidates (one per line / comma-sep).
    if (artifactType === 'shortlist') {
      const items = artifactContent
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      onAddShortList?.(artifactTitle.trim(), items);
    } else {
      if (!artifactContent.trim()) return;
      onAddArtifact({
        title: artifactTitle.trim(),
        content: artifactContent.trim(),
        type: artifactType,
      });
    }
    resetArtifactForm();
  };

  const handleAddTodo = (e) => {
    e.preventDefault();
    if (!todoInput.trim()) return;
    onAddTodo(todoInput.trim());
    setTodoInput('');
  };

  // Upload an image directly into an image artifact: resize on the client, POST
  // to the image endpoint, and drop the returned URL into the content field.
  const handleArtifactImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setArtifactImgError('');
    setArtifactImgUploading(true);
    try {
      const resized = await resizeImage(file, { maxDimension: 1600 });
      const token = localStorage.getItem('symphonate-token');
      const formData = new FormData();
      formData.append('file', resized);
      const res = await fetch(`${SERVER_URL}/api/upload-icon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Upload failed');
      }
      const data = await res.json();
      setArtifactContent(data.url);
      if (!artifactTitle.trim()) setArtifactTitle(file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setArtifactImgError(err.message || 'Upload failed');
    } finally {
      setArtifactImgUploading(false);
    }
  };

  const handleAddDecision = (e) => {
    e.preventDefault();
    if (!decisionTitle.trim() || !decisionDescription.trim()) return;
    onLogDecision?.(decisionTitle.trim(), decisionDescription.trim());
    setDecisionTitle('');
    setDecisionDescription('');
    setShowDecisionForm(false);
  };

  const handleAddShortListItem = (listId) => {
    const draft = (shortListItemDrafts[listId] || '').trim();
    if (!draft) return;
    onAddShortListItem?.(listId, draft);
    setShortListItemDrafts((prev) => ({ ...prev, [listId]: '' }));
  };

  const startEditing = (artifact) => {
    setEditingArtifact({ id: artifact.id, title: artifact.title, content: artifact.content });
    setExpandedArtifact(artifact.id);
  };

  const saveEdit = () => {
    if (!editingArtifact) return;
    onUpdateArtifact(editingArtifact.id, {
      title: editingArtifact.title,
      content: editingArtifact.content,
    });
    setEditingArtifact(null);
  };

  const cancelEdit = () => {
    setEditingArtifact(null);
  };

  const showVersions = (artifactId) => {
    if (versionHistory?.artifactId === artifactId) {
      setVersionHistory(null);
      return;
    }
    onGetArtifactVersions(artifactId, (response) => {
      if (response?.success) {
        setVersionHistory({ artifactId, versions: response.versions });
      }
    });
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'code':
        return '</>';
      case 'link':
        return '#';
      case 'document':
        return 'D';
      case 'diagram':
        return 'G';
      case 'checklist':
        return 'L';
      case 'image':
        return '🖼';
      case 'quiz':
        return '?';
      case 'poll':
        return '📊';
      case 'shortlist':
        return '★';
      default:
        return 'T';
    }
  };

  const renderArtifactContent = (artifact) => {
    if (artifact.type === 'link') {
      return (
        <a
          href={artifact.content}
          title={artifact.content}
          target="_blank"
          rel="noopener noreferrer"
        >
          {artifact.title || artifact.content}
        </a>
      );
    }
    if (artifact.type === 'document') {
      return (
        <div className="artifact-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{artifact.content}</ReactMarkdown>
        </div>
      );
    }
    if (artifact.type === 'image') {
      const src = resolveImageSrc(artifact.content);
      return src ? (
        <a href={src} target="_blank" rel="noopener noreferrer" className="artifact-image-link">
          <img
            className="artifact-image"
            src={src}
            alt={artifact.title || 'image'}
            loading="lazy"
          />
        </a>
      ) : (
        <p className="empty-state">No image URL set.</p>
      );
    }
    if (artifact.type === 'quiz') {
      return <QuizCard content={artifact.content} title={artifact.title} />;
    }
    if (artifact.type === 'poll') {
      return (
        <PollCard
          content={artifact.content}
          title={artifact.title}
          artifactId={artifact.id}
          currentUserId={currentUserId}
          onVote={onVotePoll}
          onClose={onClosePoll}
        />
      );
    }
    if (artifact.type === 'diagram') {
      return <Mermaid content={artifact.content} />;
    }
    if (artifact.type === 'checklist') {
      const interactive = typeof onToggleChecklistItem === 'function';
      return (
        <div className="artifact-checklist">
          {artifact.content.split('\n').map((line, i) => {
            const checked = /^\s*-\s*\[[xX]\]/.test(line);
            const text = line.replace(/^\s*-\s*\[[ xX]\]\s*/, '').replace(/^\s*-\s+/, '');
            if (!text.trim()) return null;
            return (
              <label key={i} className={`checklist-item ${checked ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly={!interactive}
                  onChange={interactive ? () => onToggleChecklistItem(artifact.id, i) : undefined}
                />
                <LinkedLabel text={text} />
              </label>
            );
          })}
        </div>
      );
    }
    if (artifact.type === 'note') {
      const lines = (artifact.content || '').split('\n');
      return (
        <div className="artifact-note">
          {lines.map((line, i) => (
            <div key={i} className="artifact-note-line">
              {line.trim() ? <LinkedLabel text={line} /> : ' '}
            </div>
          ))}
        </div>
      );
    }
    // code, default
    return <pre>{artifact.content}</pre>;
  };

  return (
    <aside className="right-panel">
      <div className="panel-tabs">
        <button
          className={`panel-tab ${activeTab === 'decisions' ? 'active' : ''}`}
          onClick={() => setActiveTab('decisions')}
        >
          Decisions ({decisions.filter((d) => d.status === 'active').length})
        </button>
        {onAddTodo && (
          <button
            className={`panel-tab ${activeTab === 'todos' ? 'active' : ''}`}
            onClick={() => setActiveTab('todos')}
          >
            To-dos ({todos.length})
          </button>
        )}
        <button
          className={`panel-tab ${activeTab === 'artifacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('artifacts')}
        >
          Artifacts ({artifacts.length + shortLists.length})
        </button>
      </div>

      <div className="panel-content">
        {activeTab === 'artifacts' && (
          <div className="artifacts-tab">
            {!showArtifactForm && (
              <button className="btn-add" onClick={() => setShowArtifactForm(true)}>
                + Add Artifact
              </button>
            )}

            {showArtifactForm && (
              <form className="artifact-form" onSubmit={handleAddArtifact}>
                <select
                  value={artifactType}
                  onChange={(e) => setArtifactType(e.target.value)}
                  className="artifact-type-select"
                >
                  <option value="note">Note</option>
                  <option value="code">Code</option>
                  <option value="link">Link</option>
                  <option value="document">Document</option>
                  <option value="diagram">Diagram</option>
                  <option value="image">Image</option>
                  <option value="quiz">Quiz</option>
                  <option value="poll">Poll</option>
                  {onAddShortList && <option value="shortlist">Short list</option>}
                </select>
                <input
                  type="text"
                  value={artifactTitle}
                  onChange={(e) => setArtifactTitle(e.target.value)}
                  placeholder={
                    artifactType === 'shortlist'
                      ? 'Short list title (e.g. Hotel candidates)'
                      : 'Title...'
                  }
                  className="artifact-input"
                  autoFocus
                />
                {artifactType === 'image' && (
                  <div className="artifact-image-upload">
                    <button
                      type="button"
                      className="btn-add"
                      onClick={() => artifactImgRef.current && artifactImgRef.current.click()}
                      disabled={artifactImgUploading}
                      title="Images up to 2MB; larger ones are automatically resized"
                    >
                      {artifactImgUploading ? 'Uploading…' : 'Upload image'}
                    </button>
                    <span className="artifact-image-hint">PNG/JPG · max 2MB</span>
                    <input
                      ref={artifactImgRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleArtifactImageUpload}
                    />
                    {artifactImgError && (
                      <span className="artifact-image-error">{artifactImgError}</span>
                    )}
                    {artifactContent && (
                      <img
                        className="artifact-image-preview"
                        src={resolveImageSrc(artifactContent)}
                        alt="preview"
                      />
                    )}
                  </div>
                )}
                <textarea
                  value={artifactContent}
                  onChange={(e) => setArtifactContent(e.target.value)}
                  placeholder={
                    artifactType === 'diagram'
                      ? 'graph TD\n  A[Start] --> B[End]'
                      : artifactType === 'image'
                        ? 'Image URL (or use Upload image above)'
                        : artifactType === 'quiz'
                          ? '{ "questions": [ { "question": "2 + 2 = ?", "options": ["3","4","5"], "answer": 1, "explanation": "2 + 2 = 4" } ] }'
                          : artifactType === 'poll'
                            ? '{ "question": "Ship on Friday?", "options": ["Yes","No","Needs testing"] }'
                            : artifactType === 'shortlist'
                              ? 'One candidate per line (or comma-separated). You can add, remove, and pin items after saving.'
                              : 'Content...'
                  }
                  className="artifact-textarea"
                  rows={artifactType === 'quiz' || artifactType === 'poll' ? 6 : 3}
                />
                <div className="artifact-form-actions">
                  <button type="submit" className="btn-save">
                    Save
                  </button>
                  <button type="button" className="btn-cancel" onClick={resetArtifactForm}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="artifacts-list">
              {artifacts.length === 0 && shortLists.length === 0 && !showArtifactForm && (
                <p className="empty-state">
                  No artifacts yet. Add notes, code snippets, links, or a short list to share with
                  the team.
                </p>
              )}
              {artifacts.map((artifact) => {
                const isEditing = editingArtifact?.id === artifact.id;
                const isExpanded = expandedArtifact === artifact.id;
                return (
                  <div key={artifact.id} className={`artifact-card ${artifact.type}`}>
                    <div
                      className="artifact-header"
                      onClick={() => setExpandedArtifact(isExpanded ? null : artifact.id)}
                    >
                      <span className="artifact-type-badge">{typeIcon(artifact.type)}</span>
                      <span className="artifact-title">{artifact.title}</span>
                      <div className="artifact-actions">
                        {onUpdateArtifact && (
                          <button
                            className="btn-edit-artifact"
                            onClick={(e) => {
                              e.stopPropagation();
                              isEditing ? cancelEdit() : startEditing(artifact);
                            }}
                            title={isEditing ? 'Cancel edit' : 'Edit'}
                          >
                            {isEditing ? <IconClose size={13} /> : <IconEdit size={13} />}
                          </button>
                        )}
                        <button
                          className="btn-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveArtifact(artifact.id);
                          }}
                        >
                          x
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div
                        className={`artifact-body ${artifact.type === 'code' ? 'code-block' : ''}`}
                      >
                        {isEditing ? (
                          <div className="artifact-edit-form">
                            <input
                              type="text"
                              value={editingArtifact.title}
                              onChange={(e) =>
                                setEditingArtifact({ ...editingArtifact, title: e.target.value })
                              }
                              className="artifact-input"
                              placeholder="Title..."
                            />
                            <textarea
                              value={editingArtifact.content}
                              onChange={(e) =>
                                setEditingArtifact({ ...editingArtifact, content: e.target.value })
                              }
                              className="artifact-textarea"
                              rows={5}
                            />
                            <div className="artifact-form-actions">
                              <button className="btn-save" onClick={saveEdit}>
                                Save
                              </button>
                              <button className="btn-cancel" onClick={cancelEdit}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          renderArtifactContent(artifact)
                        )}
                        {artifact.sourceMessageId && (
                          <div className="artifact-sources">
                            <span className="artifact-sources-label">From:</span>
                            <button
                              type="button"
                              className="artifact-source-link"
                              onClick={() => onJumpToMessage?.(artifact.sourceMessageId)}
                              title={`Jump to message ${artifact.sourceMessageId.slice(0, 8)}`}
                            >
                              #{artifact.sourceMessageId.slice(0, 6)}
                            </button>
                          </div>
                        )}
                        <div className="artifact-footer">
                          <span className="artifact-meta">
                            by {artifact.createdBy}
                            {artifact.createdAt &&
                              ` · ${new Date(artifact.createdAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}`}
                            {artifact.updatedBy && ` · edited by ${artifact.updatedBy}`}
                          </span>
                          {onGetArtifactVersions && (
                            <button
                              className="btn-versions"
                              onClick={() => showVersions(artifact.id)}
                            >
                              history
                            </button>
                          )}
                        </div>
                        {versionHistory?.artifactId === artifact.id &&
                          versionHistory.versions.length > 0 && (
                            <div className="version-history">
                              <div className="version-header">Version History</div>
                              {versionHistory.versions.map((v) => (
                                <div key={v.id} className="version-entry">
                                  <span className="version-meta">
                                    {new Date(v.updatedAt).toLocaleString()} by {v.updatedBy}
                                  </span>
                                  <pre className="version-content">
                                    {v.content.length > 200
                                      ? v.content.slice(0, 200) + '...'
                                      : v.content}
                                  </pre>
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}

              {shortLists.map((list) => {
                const listKey = `shortlist:${list.id}`;
                const listExpanded = expandedArtifact === listKey;
                return (
                  <div
                    key={list.id}
                    className={`artifact-card shortlist shortlist-card ${
                      listExpanded ? 'expanded' : 'collapsed'
                    }`}
                  >
                    <div
                      className="shortlist-header"
                      onClick={() => setExpandedArtifact(listExpanded ? null : listKey)}
                    >
                      <span className="artifact-type-badge">{typeIcon('shortlist')}</span>
                      <span className="shortlist-title">{list.title}</span>
                      {onRemoveShortList && (
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveShortList(list.id);
                          }}
                          title="Remove short list"
                        >
                          x
                        </button>
                      )}
                      <span className="artifact-collapse-caret" aria-hidden="true">
                        {listExpanded ? '▾' : '▸'}
                      </span>
                    </div>
                    {listExpanded && (
                      <>
                        <ul className="shortlist-items">
                          {(list.items || []).map((item) => (
                            <li
                              key={item.id}
                              className={`shortlist-item ${item.pinned ? 'pinned' : ''}`}
                            >
                              {onToggleShortListItemPin && (
                                <button
                                  type="button"
                                  className="shortlist-pin"
                                  onClick={() => onToggleShortListItemPin(list.id, item.id)}
                                  title={item.pinned ? 'Unpin candidate' : 'Pin as front-runner'}
                                >
                                  {item.pinned ? '★' : '☆'}
                                </button>
                              )}
                              <LinkedLabel text={item.label} className="shortlist-item-label" />
                              {item.notes && (
                                <LinkedLabel text={item.notes} className="shortlist-item-notes" />
                              )}
                              {onRemoveShortListItem && (
                                <button
                                  type="button"
                                  className="btn-remove shortlist-remove-item"
                                  onClick={() => onRemoveShortListItem(list.id, item.id)}
                                  title="Remove candidate"
                                >
                                  x
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                        {onAddShortListItem && (
                          <form
                            className="shortlist-add-form"
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleAddShortListItem(list.id);
                            }}
                          >
                            <input
                              type="text"
                              value={shortListItemDrafts[list.id] || ''}
                              onChange={(e) =>
                                setShortListItemDrafts((prev) => ({
                                  ...prev,
                                  [list.id]: e.target.value,
                                }))
                              }
                              placeholder="Add a candidate..."
                              className="shortlist-add-input"
                            />
                            <button type="submit" className="btn-save">
                              Add
                            </button>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'todos' && (
          <div className="todos-tab">
            {todos.length === 0 ? (
              <p className="empty-state">
                No to-dos yet. Add tasks for the team — the AI can also capture them from the
                conversation.
              </p>
            ) : (
              <div className="artifact-checklist todos-list">
                {todos.map((todo) => (
                  <label
                    key={todo.id}
                    className={`checklist-item ${todo.completed ? 'checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!todo.completed}
                      onChange={() => onToggleTodo(todo.id)}
                    />
                    <LinkedLabel text={todo.text} className="todo-text" />
                    {onRemoveTodo && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => onRemoveTodo(todo.id)}
                        title="Remove task"
                      >
                        x
                      </button>
                    )}
                  </label>
                ))}
              </div>
            )}
            {onAddTodo && (
              <form className="todo-form" onSubmit={handleAddTodo}>
                <input
                  type="text"
                  value={todoInput}
                  onChange={(e) => setTodoInput(e.target.value)}
                  placeholder="Add a task..."
                  className="todo-input"
                />
                <button type="submit" className="btn-add-todo" disabled={!todoInput.trim()}>
                  +
                </button>
              </form>
            )}
          </div>
        )}

        {activeTab === 'decisions' && (
          <div className="decisions-tab">
            <div className={`ai-context-card ${aiContextOpen ? 'open' : 'collapsed'}`}>
              <button
                type="button"
                className="ai-context-toggle"
                onClick={() => setAiContextOpen((v) => !v)}
                aria-expanded={aiContextOpen}
              >
                <span className="ai-context-icon" aria-hidden="true">
                  ✨
                </span>
                <span className="ai-context-title">AI Context</span>
                <span className="ai-context-chevron">{aiContextOpen ? '▾' : '▸'}</span>
              </button>
              {aiContextOpen && (
                <div className="ai-context-body">
                  <div className="ai-context-row">
                    <span className="ai-context-row-label">This thread:</span>
                    <span className="ai-context-row-value">
                      {artifacts.length} {artifacts.length === 1 ? 'artifact' : 'artifacts'} ·{' '}
                      {decisions.filter((d) => d.status === 'active').length}{' '}
                      {decisions.filter((d) => d.status === 'active').length === 1
                        ? 'decision'
                        : 'decisions'}{' '}
                      · {shortLists.length} {shortLists.length === 1 ? 'shortlist' : 'shortlists'}
                    </span>
                  </div>
                  {siblingThreadSummary.length > 0 ? (
                    <div className="ai-context-siblings">
                      <span className="ai-context-row-label">Sibling threads:</span>
                      <ul className="ai-context-sibling-list">
                        {siblingThreadSummary
                          .filter(
                            (s) =>
                              s.artifactCount > 0 || s.decisionCount > 0 || s.shortListCount > 0,
                          )
                          .slice(0, 6)
                          .map((s) => (
                            <li key={s.id}>
                              <span className="ai-context-sibling-name">#{s.slug || s.name}</span>
                              <span className="ai-context-sibling-stats">
                                {s.decisionCount > 0 &&
                                  `${s.decisionCount} ${s.decisionCount === 1 ? 'decision' : 'decisions'}`}
                                {s.decisionCount > 0 &&
                                  (s.artifactCount > 0 || s.shortListCount > 0) &&
                                  ' · '}
                                {s.artifactCount > 0 &&
                                  `${s.artifactCount} ${s.artifactCount === 1 ? 'artifact' : 'artifacts'}`}
                                {s.artifactCount > 0 && s.shortListCount > 0 && ' · '}
                                {s.shortListCount > 0 &&
                                  `${s.shortListCount} ${s.shortListCount === 1 ? 'shortlist' : 'shortlists'}`}
                              </span>
                            </li>
                          ))}
                      </ul>
                      <p className="ai-context-hint">
                        Tip: reference a sibling with <code>#thread-slug</code> to pull in its full
                        content for the next AI turn.
                      </p>
                    </div>
                  ) : (
                    <p className="ai-context-empty">
                      No sibling threads with content yet — the AI is drawing only on this thread.
                    </p>
                  )}
                </div>
              )}
            </div>

            {!showDecisionForm && onLogDecision && (
              <button className="btn-add" onClick={() => setShowDecisionForm(true)}>
                + Log Decision
              </button>
            )}

            {showDecisionForm && (
              <form className="decision-form" onSubmit={handleAddDecision}>
                <input
                  type="text"
                  value={decisionTitle}
                  onChange={(e) => setDecisionTitle(e.target.value)}
                  placeholder="Decision title..."
                  className="artifact-input"
                  autoFocus
                />
                <textarea
                  value={decisionDescription}
                  onChange={(e) => setDecisionDescription(e.target.value)}
                  placeholder="What was decided, and why..."
                  className="artifact-textarea"
                  rows={3}
                />
                <div className="artifact-form-actions">
                  <button type="submit" className="btn-save">
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => {
                      setShowDecisionForm(false);
                      setDecisionTitle('');
                      setDecisionDescription('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="decisions-list">
              {decisions.length === 0 && !showDecisionForm && (
                <p className="empty-state">
                  No decisions logged yet. Record key conclusions the team reaches — the AI can help
                  by suggesting them automatically.
                </p>
              )}
              {decisions.map((decision) => (
                <div key={decision.id} className={`decision-card status-${decision.status}`}>
                  <div className="decision-header">
                    <LinkedLabel text={decision.title} className="decision-title" />
                    <div className="decision-actions">
                      {onUpdateDecisionStatus && (
                        <select
                          value={decision.status}
                          className="decision-status-select"
                          onChange={(e) => onUpdateDecisionStatus(decision.id, e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="revisited">Revisited</option>
                          <option value="reversed">Reversed</option>
                        </select>
                      )}
                      {onRemoveDecision && (
                        <button
                          className="btn-remove"
                          onClick={() => onRemoveDecision(decision.id)}
                          title="Remove decision"
                        >
                          x
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="decision-body">
                    <LinkedLabel text={decision.description} />
                  </div>
                  {decisionSources[decision.id] && decisionSources[decision.id].length > 0 && (
                    <div className="decision-sources">
                      <span className="decision-sources-label">From:</span>
                      {decisionSources[decision.id].map((mid) => (
                        <button
                          key={mid}
                          type="button"
                          className="decision-source-link"
                          onClick={() => onJumpToMessage?.(mid)}
                          title={`Jump to message ${mid.slice(0, 8)}`}
                        >
                          #{mid.slice(0, 6)}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="decision-footer">
                    <span className="decision-meta">
                      by {decision.decidedBy} ·{' '}
                      {new Date(decision.decidedAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    {workspaceId && (
                      <span className="decision-share">
                        {decisionShares[decision.id]?.url ? (
                          <>
                            <a
                              className="decision-share-link"
                              href={decisionShares[decision.id].url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {decisionShares[decision.id].copied ? 'Link copied!' : 'Public link'}
                            </a>
                            <button
                              type="button"
                              className="decision-share-btn"
                              onClick={() => handleUnshareDecision(decision.id)}
                              title="Revoke the public link"
                            >
                              Unshare
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="decision-share-btn"
                            onClick={() => handleShareDecision(decision.id)}
                            title="Create a public read-only link and copy it"
                          >
                            <IconShare size={12} />{' '}
                            {decisionShares[decision.id]?.error ? 'Share failed' : 'Share'}
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

export default RightPanel;
